import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import React, { useState, useContext, useMemo, useCallback, useEffect, useRef } from 'react';
import Box from '@material-ui/core/Box';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { CrestLoader } from '@scoir/college-image-components';
import { ComplexCard } from '@scoir/card';
import PropTypes from 'prop-types';
import cn from 'classnames';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import Typography from '@material-ui/core/Typography';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisH, faHashtag, faClock, faVideo, faMapMarkerAlt, faBed, faCalendarAlt, faAward, faDollarSign, faUser, faPaperPlane, faExternalLink, faArrowLeft as faArrowLeft$1, faArrowRight as faArrowRight$1, faTelescope, faSparkles } from '@fortawesome/pro-solid-svg-icons';
import Collapse from '@material-ui/core/Collapse';
import Button from '@material-ui/core/Button';
import { Pill } from '@scoir/pills';
import ReactPlayer from 'react-player';
import isUndefined from 'lodash/isUndefined';
import size from 'lodash/size';
import reject from 'lodash/reject';
import map from 'lodash/map';
import Grid from '@material-ui/core/Grid';
import { faRepeat, faDiploma, faArrowLeft, faArrowRight, faMapMarkerAlt as faMapMarkerAlt$1, faVideo as faVideo$1, faCalendar, faRedo, faAward as faAward$1, faSnowflake, faFlower, faSun, faLeaf } from '@fortawesome/pro-regular-svg-icons';
import { format, isValid } from 'date-fns';
import min from 'lodash/min';
import max from 'lodash/max';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import { Link } from 'react-router-dom';
import InformationalModal from '@scoir/components-core/dist/components/InformationalModal';
import AvatarWithTitle from '@scoir/avatar-with-title';
import Pagination from '@material-ui/lab/Pagination';
import { SectionTitle } from '@scoir/headers';
import Container from '@material-ui/core/Container';
import { EmptyStateWithAction, BasicEmptyState } from '@scoir/empty-state';
import Masonry from 'react-masonry-component';
import { useMetadata, Metadata } from '@scoir/tracking';
import Paper from '@material-ui/core/Paper';
import Backdrop from '@material-ui/core/Backdrop';
import CircularProgress from '@material-ui/core/CircularProgress';
import Tabs, { ScrollHideAppBar } from '@scoir/tabs';
import { ChipSelector, academicFocusIcons, SelectableIconChips, Dropdown } from '@scoir/college-search';
import ListSubheader from '@material-ui/core/ListSubheader';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Checkbox from '@material-ui/core/Checkbox';
import Alert from '@material-ui/lab/Alert';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import omit from 'lodash/omit';
import { MultiSectionDialog } from '@scoir/modals';
import Avatar, { ICON_COLOR } from '@scoir/avatar';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol */


function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

const CONTENT_TYPES = {
  STUDENT_LIFE: 'StudentLife',
  OVERVIEW: 'CollegeOverview',
  ACADEMICS: 'Academics',
  ADMISSIONS: 'Admissions',
  COST_FINANCIAL_AID: 'CostAndFinancialAid',
  SESSIONS: 'Sessions',
  PROGRAMS: 'Programs'
};

/**
 * This hook provides generic open/close functionality.
 * @param {bool} initial
 * @returns Object { open: true, onOpen: fn, onClose: fn }
 */
const useOpenClose = initial => {
  const [open, setOpen] = useState(initial);
  return {
    open,
    onOpen: () => setOpen(true),
    onClose: () => setOpen(false)
  };
};

const showHideContext = /*#__PURE__*/React.createContext();
const ShowHideContextProvider = _ref => {
  let {
    children,
    isExpandedByDefault,
    hideButton
  } = _ref;
  const {
    open: isExpanded,
    onClose,
    onOpen
  } = useOpenClose(isExpandedByDefault || hideButton);
  return /*#__PURE__*/jsx(showHideContext.Provider, {
    value: {
      isExpanded,
      onClose,
      onOpen
    },
    children: children
  });
};
ShowHideContextProvider.propTypes = {
  children: PropTypes.node,
  hideButton: PropTypes.bool,
  isExpandedByDefault: PropTypes.bool
};

const ShowHideContainer = _ref => {
  let {
    children,
    collapsedHeight
  } = _ref;
  const {
    isExpanded
  } = useContext(showHideContext);
  return /*#__PURE__*/jsx(Collapse, {
    in: isExpanded,
    collapsedSize: collapsedHeight,
    "aria-hidden": !isExpanded,
    children: children
  });
};
ShowHideContainer.propTypes = {
  children: PropTypes.node,
  collapsedHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

const ShowHideTrigger = _ref => {
  let {
    children,
    onExpand = noop,
    onClickOverride,
    ...rest
  } = _ref;
  const {
    isExpanded,
    onClose,
    onOpen
  } = useContext(showHideContext);
  const onClick = onClickOverride || (isExpanded ? onClose : () => {
    onOpen();
    onExpand();
  });
  return /*#__PURE__*/jsx(Button, {
    color: "primary",
    onClick: onClick,
    variant: "text",
    ...rest,
    "aria-label": "toggle additional content",
    children: children
  });
};
ShowHideTrigger.propTypes = {
  children: PropTypes.node,
  onExpand: PropTypes.func,
  onClickOverride: PropTypes.func
};

const useStyles$c = makeStyles(() => ({
  showHideTrigger: {
    backgroundColor: 'rgba(0,0,0,0.04)',
    borderRadius: '4px',
    color: 'rgba(51, 51, 51, 0.38)',
    width: '36px',
    '&.MuiButton-root': {
      minWidth: 'initial'
    }
  }
}));
const EllipsesTruncation = _ref => {
  let {
    list = [],
    ListItemComponent,
    getListItemProps = noop,
    truncateCount,
    onShowMore
  } = _ref;
  const styles = useStyles$c();
  const hasItemsToHide = useMemo(() => list.length > truncateCount, [list.length, truncateCount]);
  const [isShowHideTriggerHidden, setIsShowHideTriggerHidden] = useState(!hasItemsToHide);
  const previewList = useMemo(() => {
    return hasItemsToHide ? list.slice(0, truncateCount) : list;
  }, [list, truncateCount]);
  const restOfList = useMemo(() => {
    return hasItemsToHide ? list.slice(truncateCount) : [];
  }, [truncateCount, list, hasItemsToHide]);
  const startList = useMemo(() => {
    return previewList.map((item, key) => {
      return /*#__PURE__*/jsx(ListItemComponent, {
        tabIndex: "0",
        ...getListItemProps(item)
      }, key + '_a');
    });
  }, [previewList, ListItemComponent]);
  const endList = useMemo(() => {
    return restOfList.map((item, key) => {
      return /*#__PURE__*/jsx(ListItemComponent, {
        tabIndex: "0",
        ...getListItemProps(item)
      }, key + '_b');
    });
  }, [restOfList, ListItemComponent]);
  const onExpand = useCallback(() => {
    return onShowMore ? onShowMore() : setIsShowHideTriggerHidden(true);
  }, [onShowMore, setIsShowHideTriggerHidden]);
  return /*#__PURE__*/jsxs(ShowHideContextProvider, {
    children: [startList, !hasItemsToHide ? null : /*#__PURE__*/jsxs(Fragment, {
      children: [isShowHideTriggerHidden ? null : /*#__PURE__*/jsx(ShowHideTrigger, {
        onExpand: onExpand,
        onClickOverride: onShowMore,
        className: styles.showHideTrigger,
        children: /*#__PURE__*/jsx(FontAwesomeIcon, {
          icon: faEllipsisH
        })
      }), /*#__PURE__*/jsx(ShowHideContainer, {
        children: endList
      })]
    })]
  });
};
EllipsesTruncation.propTypes = {
  getListItemProps: PropTypes.func,
  list: PropTypes.array.isRequired,
  ListItemComponent: PropTypes.elementType,
  truncateCount: PropTypes.number.isRequired,
  onShowMore: PropTypes.func
};

const Tag = _ref => {
  let {
    children,
    showIcon,
    ...rest
  } = _ref;
  return /*#__PURE__*/jsxs(Pill, {
    ...rest,
    color: 'info',
    children: [!showIcon ? null : /*#__PURE__*/jsx(FontAwesomeIcon, {
      icon: faHashtag
    }), "\xA0", /*#__PURE__*/jsx(Box, {
      component: "span",
      lineHeight: "1em",
      children: /*#__PURE__*/jsx(Typography, {
        variant: "caption",
        children: children
      })
    })]
  });
};
Tag.propTypes = {
  children: PropTypes.node,
  showIcon: PropTypes.bool
};

const VISIBLE_TAG_COUNT = 8;
const TagPillList = _ref => {
  let {
    category = '',
    list,
    visibleTagCount = VISIBLE_TAG_COUNT,
    getTagPillProps = noop,
    additionalOnClick = noop,
    onShowMore,
    source,
    type
  } = _ref;
  const getListItemProps = useCallback(tag => {
    const tagPillProps = getTagPillProps(tag, source, type);
    const className = cn('rcc-tag-pill-list-pill', get(tagPillProps, 'className'));
    const onClick = get(tagPillProps, 'onClick');
    return {
      key: tag + '_' + Math.random(),
      children: tag,
      ...tagPillProps,
      className,
      onClick: e => {
        if (onClick) {
          additionalOnClick(e, tag);
          onClick(e, tag);
        }
      }
    };
  }, [source]);
  const hasCategory = useMemo(() => !isEmpty(category), [category]);
  return /*#__PURE__*/jsxs(Box, {
    className: "rcc-tag-pill-list",
    width: "100%",
    children: [hasCategory && /*#__PURE__*/jsx(Typography, {
      className: 'rcc-tag-pill-list-heading',
      variant: "subtitle2",
      children: category
    }), /*#__PURE__*/jsx(EllipsesTruncation, {
      list: list,
      ListItemComponent: Tag,
      getListItemProps: getListItemProps,
      truncateCount: visibleTagCount,
      onShowMore: onShowMore
    })]
  });
};
TagPillList.propTypes = {
  category: PropTypes.string,
  list: PropTypes.array,
  visibleTagCount: PropTypes.number,
  getTagPillProps: PropTypes.func,
  additionalOnClick: PropTypes.func,
  onShowMore: PropTypes.func,
  source: PropTypes.string,
  type: PropTypes.oneOf(Object.values(CONTENT_TYPES)).isRequired
};

const useStyles$b = makeStyles(() => ({
  avatar: {
    height: 40,
    width: 40
  },
  action: {
    fontSize: 15
  }
}));
const VIEW_POSTS_CLICK_EVENT_SOURCE$2 = 'ContentCardTag';
const ContentItemCard = _a => {
  var {
      description,
      collegeEvent,
      collegeName,
      collegeLocation,
      scid,
      editComponent,
      type,
      imageUrl,
      isSelected = false,
      onToggleSelect,
      foci = [],
      tags = [],
      title,
      getTagPillProps,
      forYouPill,
      showSelectableStyles = false,
      hostedRootPath,
      onShowMoreTags,
      contentImgAlt
    } = _a,
    rest = __rest(_a, ["description", "collegeEvent", "collegeName", "collegeLocation", "scid", "editComponent", "type", "imageUrl", "isSelected", "onToggleSelect", "foci", "tags", "title", "getTagPillProps", "forYouPill", "showSelectableStyles", "hostedRootPath", "onShowMoreTags", "contentImgAlt"]);
  const targets = useMemo(() => [...foci, ...tags], []);
  const classes = useStyles$b();
  const hasHeader = useMemo(() => !!collegeName && !!collegeLocation && !!scid, [collegeName, collegeLocation, scid]);
  const headerProps = useMemo(() => {
    return !hasHeader ? undefined : {
      title: collegeName,
      subheader: collegeLocation,
      avatar: jsx(CrestLoader, {
        scid: scid,
        className: classes.avatar,
        alt: ""
      })
    };
  }, [hasHeader, collegeName, collegeLocation, scid, classes.avatar]);
  const labels = useMemo(() => {
    return !targets.length ? null : jsxs(Box, {
      width: "100%",
      position: "relative",
      zIndex: "5",
      onClick: e => e.stopPropagation(),
      children: [forYouPill, jsx(TagPillList, {
        list: targets,
        visibleTagCount: 4,
        getTagPillProps: getTagPillProps,
        onShowMore: onShowMoreTags,
        source: VIEW_POSTS_CLICK_EVENT_SOURCE$2,
        type: type
      })]
    }, "pills");
  }, [targets, forYouPill]);
  return jsx(ComplexCard, Object.assign({
    headerProps: headerProps,
    hasHeaderDivider: hasHeader,
    headerToLink: hasHeader ? `${hostedRootPath}/colleges/${scid}` : undefined,
    mediaImageSrc: imageUrl,
    maxWidth: "100%",
    contentTitle: title,
    contentBody: description,
    labels: labels,
    editControls: editComponent,
    onSelectCard: onToggleSelect,
    isSelected: isSelected,
    showSelectableStyles: showSelectableStyles,
    contentTitleTruncate: true,
    contentBodyTruncate: true,
    eventComponent: collegeEvent,
    contentImgAlt: contentImgAlt
  }, rest));
};

const expressions = {
  youtube: /^https?:\/\/(?:www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/,
  vimeo: /^https?:\/\/(?:www\.)?vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/([^/]*)\/videos\/|album\/(\d+)\/video\/|)([0-9]{5,11})$/
};
const getPlatformAndId = url => {
  const conf = {};
  if (expressions.youtube.test(url)) {
    conf.platform = 'youtube';
    conf.id = url.match(expressions.youtube)[1];
  }
  if (expressions.vimeo.test(url)) {
    conf.platform = 'vimeo';
    conf.id = url.match(expressions.vimeo)[3];
  }
  return conf;
};
const isVideoLink = url => expressions.youtube.test(url) || expressions.vimeo.test(url);
const VideoPreview = _ref => {
  let {
    videoStyles,
    url,
    onPlayVideo
  } = _ref;
  const {
    platform
  } = getPlatformAndId(url);
  if (platform === 'youtube' || platform == 'vimeo') {
    return /*#__PURE__*/jsx(ReactPlayer, {
      controls: true,
      url: url,
      style: {
        border: 0,
        maxHeight: '200px',
        maxWidth: '100%',
        display: 'block',
        background: '#000',
        borderRadius: '5px',
        marginTop: '20px',
        marginBottom: '20px',
        ...videoStyles
      },
      onPlay: onPlayVideo
    });
  } else {
    return null;
  }
};
VideoPreview.propTypes = {
  url: PropTypes.string.isRequired,
  videoStyles: PropTypes.object,
  onPlayVideo: PropTypes.func.isRequired
};
VideoPreview.defaultProps = {
  url: ''
};
VideoPreview.isVideoLink = isVideoLink;

function _defineProperty(obj, key, value) {
  key = _toPropertyKey(key);
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _toPrimitive(input, hint) {
  if (typeof input !== "object" || input === null) return input;
  var prim = input[Symbol.toPrimitive];
  if (prim !== undefined) {
    var res = prim.call(input, hint || "default");
    if (typeof res !== "object") return res;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return (hint === "string" ? String : Number)(input);
}
function _toPropertyKey(arg) {
  var key = _toPrimitive(arg, "string");
  return typeof key === "symbol" ? key : String(key);
}

const formatRecurringDate = dateStr => {
  const date = new Date(dateStr);
  return format(date, 'EEEE, MMMM d, yyyy');
};
const getTimestamp = function () {
  let startTime = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  let endTime = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
  let timestamp = '';
  const start = new Date(startTime);
  const end = new Date(endTime);
  if (isValid(start) && isValid(end)) {
    timestamp = `${format(start, 'EEEE, MMMM d, h:mm a')} - ${format(end, 'h:mm a')}`;
  }
  return timestamp;
};
const formatCredits = (minCredits, maxCredits) => {
  if (minCredits === undefined || maxCredits === undefined) return 'Credits Awarded';
  if (minCredits === maxCredits) {
    return `${minCredits} Credit${minCredits > 1 ? 's' : ''}`;
  }
  return `${minCredits}-${maxCredits} Credits`;
};
const formatEligibility = eligibility => {
  // If only one eligibility, return it
  if (eligibility.length === 1) {
    return eligibility[0];
  }
  const sortedEligibilityArr = [...eligibility];
  // Sort the array
  sortedEligibilityArr.sort((a, b) => {
    if (a === 'High School Graduate') return 1;
    if (b === 'High School Graduate') return -1;
    return parseInt(a.split('th')[0]) - parseInt(b.split('th')[0]);
  });
  // If multiple eligibilities, convert the strings to numbers
  const eligibilityNumbers = sortedEligibilityArr.map(e => {
    if (e === 'High School Graduate') return 13;
    return parseInt(e.split('th')[0]);
  });
  // Check if the numbers are consecutive
  const isConsecutive = eligibilityNumbers.every((e, i, arr) => {
    if (i === 0) return true;
    return e - arr[i - 1] === 1;
  });
  // If consecutive, return the first and last eligibility
  if (isConsecutive) {
    if (sortedEligibilityArr.includes('High School Graduate')) {
      return `${sortedEligibilityArr[0]} grade - High School Graduate`;
    }
    return `${sortedEligibilityArr[0]} grade - ${sortedEligibilityArr[sortedEligibilityArr.length - 1]} grade`;
  }
  // If not consecutive, but end in Graduate, return all eligibilities
  if (sortedEligibilityArr.includes('High School Graduate')) {
    return sortedEligibilityArr.join(' grade, ');
  }
  // Default - if not consecutive, return all eligibilities with grade
  return `${sortedEligibilityArr.join(' grade, ')} grade`;
};

const useStyles$a = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: _ref => {
      let {
        stackSegments
      } = _ref;
      return stackSegments ? 'column' : 'row';
    }
  },
  icon: {
    fontSize: theme.spacing(2),
    /** Override fontAwesome width */
    width: `${theme.spacing(2)}px!important`
  },
  segmentWrapper: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    minWidth: '50%',
    padding: _ref2 => {
      let {
        stackSegments
      } = _ref2;
      return stackSegments ? theme.spacing(1, 0) : theme.spacing(1, 1, 1, 0);
    },
    width: _ref3 => {
      let {
        stackSegments
      } = _ref3;
      return stackSegments ? 'inherit' : '50%';
    }
  }
}));
const Event = _ref4 => {
  let {
    stackSegments,
    item
  } = _ref4;
  const classes = useStyles$a({
    stackSegments
  });
  if (isEmpty(item)) return null;
  // Date - Type = Sessions
  let dateSegment = null;
  if (item.IsRecurring || item.StartTime || item.EndTime) {
    dateSegment = jsxs(Box, {
      className: classes.segmentWrapper,
      children: [jsx(FontAwesomeIcon, {
        className: classes.icon,
        icon: item.IsRecurring ? faRepeat : faClock
      }), jsx(Typography, {
        variant: "caption",
        children: item.IsRecurring && item.Date ? `Repeats until ${formatRecurringDate(item.Date)}` : getTimestamp(item.StartTime, item.EndTime)
      })]
    });
  }
  // Location - Type = Sessions + *  R & B - Programs Only
  let locationSegment = null;
  if (item.IsVirtual || item.MeetingLocation || item.RoomAndBoardAvailable) {
    locationSegment = jsxs(Box, {
      className: classes.segmentWrapper,
      children: [jsx(FontAwesomeIcon, {
        className: classes.icon,
        icon: item.IsVirtual ? faVideo : item.MeetingLocation ? faMapMarkerAlt : faBed
      }), jsxs(Typography, {
        variant: "caption",
        children: [item.IsVirtual ? 'Virtual' : item.MeetingLocation, item.RoomAndBoardAvailable && `${item.MeetingLocation ? ' • ' : ''}Room & Board`]
      })]
    });
  }
  // Season - Type = Programs
  let seasonSegment = null;
  if (item.Season || item.SeasonDescription) {
    seasonSegment = jsxs(Box, {
      className: classes.segmentWrapper,
      children: [jsx(FontAwesomeIcon, {
        className: classes.icon,
        icon: faCalendarAlt
      }), jsx(Typography, {
        variant: "caption",
        children: item.SeasonDescription ? item.SeasonDescription : item.Season && item.Season.join(', ')
      })]
    });
  }
  // Credits - Type = Programs
  let creditSegment = null;
  if (Object.getOwnPropertyDescriptor(item, 'CreditsAwarded')) {
    creditSegment = jsxs(Box, {
      className: classes.segmentWrapper,
      sx: {
        pb: 1
      },
      children: [jsx(FontAwesomeIcon, {
        className: classes.icon,
        icon: item.CreditsAwarded ? faDiploma : faAward
      }), jsx(Typography, {
        variant: "caption",
        children: item.CreditsAwarded ? formatCredits(item.MinCredits, item.MaxCredits) : 'Non-Credit'
      })]
    });
  }
  // Cost * Aid & Scholarships - Type = Programs
  let costAidSegment = null;
  if (item.CostDescription || item.FinancialAidAvailable) {
    costAidSegment = jsxs(Box, {
      className: classes.segmentWrapper,
      children: [jsx(FontAwesomeIcon, {
        className: classes.icon,
        icon: faDollarSign
      }), jsxs(Typography, {
        variant: "caption",
        children: [item.CostDescription && item.CostDescription, item.FinancialAidAvailable && `${item.CostDescription ? ' • ' : ''}Aid & Scholarships`]
      })]
    });
  }
  // Eligibility - Type = Programs = MODAL ONLY
  let eligibilitySegment = null;
  if (item.Eligibility && item.Eligibility.length > 0) {
    eligibilitySegment = jsxs(Box, {
      className: classes.segmentWrapper,
      children: [jsx(FontAwesomeIcon, {
        className: classes.icon,
        icon: faUser
      }), jsx(Typography, {
        variant: "caption",
        children: formatEligibility(item.Eligibility)
      })]
    });
  }
  // Application Deadline - Type = Programs = MODAL ONLY
  let adminDescSegment = null;
  if (item.AdmissionDescription) {
    adminDescSegment = jsxs(Box, {
      className: classes.segmentWrapper,
      children: [jsx(FontAwesomeIcon, {
        className: classes.icon,
        icon: faPaperPlane
      }), jsx(Typography, {
        variant: "caption",
        children: item.AdmissionDescription
      })]
    });
  }
  if (!dateSegment && !locationSegment && !seasonSegment && !creditSegment && !costAidSegment && !eligibilitySegment && !adminDescSegment) {
    return null;
  }
  return jsxs(Box, {
    className: classes.container,
    px: stackSegments ? 2 : 3,
    pb: 1,
    children: [dateSegment, locationSegment, seasonSegment, creditSegment, costAidSegment, !stackSegments && eligibilitySegment, !stackSegments && adminDescSegment]
  });
};

const WARNING_TEXT = 'DynamicContentContext warning - No callback for onPlayVideo provided';
// eslint-disable-next-line no-console
const warn = () => console.warn(WARNING_TEXT);
const DynamicContentContext = /*#__PURE__*/React.createContext({
  onPlayVideo: warn
});
const withDynamicContentContext = Component => {
  const WrappedComponent = props => {
    const contextProps = useContext(DynamicContentContext);
    return /*#__PURE__*/jsx(Component, {
      ...contextProps,
      ...props
    });
  };
  return WrappedComponent;
};

const getImageUrl = item => {
  const type = get(item, 'Type');
  const imageUrl = get(item, 'ImageUrl', '');
  // Sessions don't require images to create, so if an imageURL isn't present, can assume this should be blank
  if ((!type || type === '') && imageUrl === '') return imageUrl;
  const lastModified = encodeURI(get(item, 'UpdatedDate', ''));
  const id = get(item, 'Id');
  // original college content (ie. Overview, Sessions, etc.)
  let url = `${process.env.STORYBOOK_IMAGE_URL_DOMAIN || ''}/i/img/f/cms/${id}?lastModified=${lastModified}`;
  // new content (ie. Programs)
  if (type) {
    url = `${process.env.STORYBOOK_IMAGE_URL_DOMAIN || ''}/i/img/f/cms/${type}/${id}?lastModified=${lastModified}`;
  }
  if (isUndefined(id)) {
    url = `${imageUrl}?lastModified=${lastModified}`;
  }
  return url;
};

const {
  Header,
  Footer: Footer$1
} = InformationalModal;
const getLinkElement = el => {
  const tag = get(el, 'tagName');
  const parent = get(el, 'parentElement');
  if (tag === 'A') {
    return el;
  }
  if (tag === 'P' || isUndefined(tag) || isUndefined(parent)) {
    return;
  }
  return getLinkElement(parent);
};
const MODAL_ID = 'content-display-modal-title-id';
const DEFAULT_TagLabelComponent = (_ref // eslint-disable-line react/prop-types
) => {
  let {
    className,
    key,
    children
  } = _ref;
  return /*#__PURE__*/jsx("span", {
    className: className,
    children: children
  }, key);
};
const VIEW_POSTS_CLICK_EVENT_SOURCE$1 = 'ContentCardTag';
class ContentItemModal extends React.Component {
  constructor(props) {
    super(props);
    this.elementId = `modal_${Math.random().toString(36).substr(2, 9)}`;
  }
  componentDidUpdate(prevProps) {
    const itemHasChanged = get(this.props.item, 'Id') !== get(prevProps.item, 'Id');
    const el = document.getElementById(this.elementId);
    if (itemHasChanged && el !== null) {
      el.focus();
    }
  }
  previous() {
    this.props.onChange(max([0, this.props.openItemIdx - 1]));
  }
  next() {
    this.props.onChange(min([this.getLast(), this.props.openItemIdx + 1]));
  }
  getLast() {
    return this.props.itemsCount - 1;
  }
  handleKeyDown(key) {
    if (key === 'ArrowRight') {
      this.next();
    }
    if (key === 'ArrowLeft') {
      this.previous();
    }
  }
  trackLinkClick(e, ClickedLinkWasCTA) {
    const {
      onTrackLinkClick
    } = this.props;
    if (isUndefined(onTrackLinkClick)) {
      return;
    }
    let link = getLinkElement(e.target);
    if (!link) {
      return;
    }
    onTrackLinkClick({
      ClickedLinkUrl: link.href,
      ClickedLinkLabel: link.text,
      ClickedLinkWasCTA
    });
  }
  render() {
    const {
      collegeAction,
      openItemIdx,
      itemsCount,
      item,
      hostedRootPath,
      getTagPillProps,
      onPlayVideo,
      onChange,
      type,
      collegeUrl
    } = this.props;
    const scid = get(item, 'Scid', 0);
    const tags = get(item, 'Tags', []);
    const foci = get(item, 'AcademicFocus', []);
    const collegeName = get(item, 'CollegeName');
    const isFirst = openItemIdx === 0;
    const isLast = openItemIdx === this.getLast();
    const link = get(item, 'Link');
    const title = get(item, 'Title');
    const altText = get(item, 'AltText');
    const hasTags = size(tags) > 0;
    const hasFoci = size(foci) > 0;
    const imageUrl = getImageUrl(item);
    const isSession = type === CONTENT_TYPES.SESSIONS;
    const isProgram = type === CONTENT_TYPES.PROGRAMS;
    const isVideo = isVideoLink(link);
    /** Adding program content with a link & a videoLink, need to account for both */
    const videoLink = isVideo ? link : get(item, 'VideoLink');
    const displayVideoOnly = isVideo && !isProgram;
    const url = !isUndefined(collegeUrl) ? collegeUrl : `${hostedRootPath}/colleges/${scid}`;
    const showFooter = itemsCount > 1 || !isEmpty(link) && !displayVideoOnly;
    return /*#__PURE__*/jsx(InformationalModal, {
      show: !isUndefined(item),
      className: cn(['content-display-modal-comp', 'new-modal', {
        'hide-close': isSession
      }]),
      id: this.elementId,
      theme: "seamless",
      onKeyDown: e => this.handleKeyDown(e.key),
      "aria-label": title,
      "aria-labelledby": MODAL_ID,
      onHide: () => onChange(),
      children: /*#__PURE__*/jsxs("div", {
        children: [/*#__PURE__*/jsxs(Header, {
          className: cn(['content-display-modal-header', 'shrink-padding']),
          children: [/*#__PURE__*/jsx(AvatarWithTitle, {
            avatar: /*#__PURE__*/jsx(Link, {
              to: url,
              children: /*#__PURE__*/jsx(CrestLoader, {
                scid: scid,
                className: "avatar"
              })
            }),
            title: /*#__PURE__*/jsx(Box, {
              mt: -1,
              children: /*#__PURE__*/jsxs(Typography, {
                align: "left",
                component: "span",
                variant: "body1",
                display: "block",
                "aria-label": "View College Profile",
                children: [/*#__PURE__*/jsx(Link, {
                  to: url,
                  className: "college-link",
                  children: collegeName
                }), !collegeAction ? null : /*#__PURE__*/jsxs("span", {
                  className: "college-action",
                  children: [/*#__PURE__*/jsx("span", {
                    className: "college-action-bullet",
                    children: " \u2022 "
                  }), collegeAction]
                })]
              })
            })
          }), /*#__PURE__*/jsx(Box, {
            mx: -3,
            my: 2,
            children: /*#__PURE__*/jsx(Divider, {})
          })]
        }), /*#__PURE__*/jsx("div", {
          className: "media-container",
          children: videoLink ? /*#__PURE__*/jsx(VideoPreview, {
            videoStyles: {
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0
            },
            onPlayVideo: () => onPlayVideo(item),
            url: videoLink
          }) : !imageUrl || imageUrl === '' ? null : /*#__PURE__*/jsx("img", {
            src: imageUrl,
            alt: altText || title
          })
        }), /*#__PURE__*/jsxs("div", {
          id: MODAL_ID,
          className: "content-display-modal-body",
          "aria-label": title,
          children: [/*#__PURE__*/jsx(Typography, {
            variant: "body1",
            children: /*#__PURE__*/jsx("strong", {
              className: "content-display-modal-title",
              children: title
            })
          }), /*#__PURE__*/jsx(Typography, {
            variant: "body2",
            onClick: e => this.trackLinkClick(e, false),
            dangerouslySetInnerHTML: {
              __html: get(item, 'Description')
            }
          })]
        }), /*#__PURE__*/jsx(Event, {
          item: item
        }), (hasTags || hasFoci) && /*#__PURE__*/jsx(Box, {
          className: "content-display-modal-targets",
          px: 3,
          py: 2,
          width: "100%",
          children: /*#__PURE__*/jsx(TagPillList, {
            list: [...foci, ...tags],
            visibleTagCount: 100,
            getTagPillProps: getTagPillProps,
            additionalOnClick: () => onChange(),
            source: VIEW_POSTS_CLICK_EVENT_SOURCE$1,
            type: type
          })
        }), showFooter && /*#__PURE__*/jsxs(Footer$1, {
          className: "content-display-modal-footer",
          children: [/*#__PURE__*/jsx(Divider, {
            style: {
              marginLeft: '-40px',
              marginRight: '-40px'
            }
          }), /*#__PURE__*/jsxs(Box, {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: 2,
            children: [itemsCount > 1 ? /*#__PURE__*/jsxs(Box, {
              display: "flex",
              gridGap: 16,
              alignItems: "center",
              my: 1,
              children: [/*#__PURE__*/jsx(IconButton, {
                id: "previous-button",
                color: "primary",
                size: "small",
                disabled: isFirst,
                onClick: () => this.previous(),
                children: /*#__PURE__*/jsx(FontAwesomeIcon, {
                  icon: faArrowLeft
                })
              }), /*#__PURE__*/jsxs(Typography, {
                variant: "body2",
                component: "span",
                children: [openItemIdx + 1, " of ", itemsCount]
              }), /*#__PURE__*/jsx(IconButton, {
                id: "next-button",
                color: "primary",
                size: "small",
                disabled: isLast,
                onClick: () => this.next(),
                children: /*#__PURE__*/jsx(FontAwesomeIcon, {
                  icon: faArrowRight
                })
              })]
            }) :
            
            // Needed for alignment
            jsx("div", {}), isEmpty(link) || displayVideoOnly ? null : /*#__PURE__*/jsx(Button, {
              id: "registration-button",
              color: "primary",
              component: "a",
              endIcon: /*#__PURE__*/jsx(FontAwesomeIcon, {
                style: {
                  fontSize: '18px',
                  paddingTop: '2px'
                },
                icon: faExternalLink
              }),
              href: link,
              onClick: e => this.trackLinkClick(e, true),
              rel: "noopener noreferrer",
              size: "large",
              target: "_blank",
              variant: "contained",
              children: isSession ? 'Register' : 'Learn More'
            })]
          })]
        })]
      })
    });
  }
}
_defineProperty(ContentItemModal, "propTypes", {
  collegeAction: PropTypes.node,
  hostedRootPath: PropTypes.string.isRequired,
  item: PropTypes.object,
  openItemIdx: PropTypes.number,
  itemsCount: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  getTagPillProps: PropTypes.func,
  onTrackLinkClick: PropTypes.func,
  onPlayVideo: PropTypes.func.isRequired,
  collegeUrl: PropTypes.string,
  type: PropTypes.oneOf(Object.values(CONTENT_TYPES))
});
_defineProperty(ContentItemModal, "defaultProps", {
  itemsCount: 0,
  TagLabelComponent: DEFAULT_TagLabelComponent
});
var ContentItemModal$1 = withDynamicContentContext(ContentItemModal);

const getLocation = item => {
  const cityState = reject([item.CollegeCity, item.CollegeState], isEmpty).join(', ');
  const stateCountry = reject([cityState, item.CollegeCountry], isEmpty).join(', ');
  return (cityState.includes(',') ? cityState : stateCountry) || undefined;
};
class ContentListDisplay extends React.Component {
  constructor() {
    super(...arguments);
    _defineProperty(this, "state", {
      openItemIdx: undefined
    });
  }
  selectItem(ContentItemDisplayPosition) {
    const {
      items,
      trackEvents,
      contentLaunchedVia
    } = this.props;
    const item = items[ContentItemDisplayPosition];
    this.setState({
      openItemIdx: ContentItemDisplayPosition
    });
    if (!isUndefined(trackEvents.onViewContent) && !isEmpty(item)) {
      trackEvents.onViewContent({
        Scid: item.Scid,
        ContentLaunchedVia: contentLaunchedVia,
        ContentItemId: item.Id,
        ContentItemWasTargeted: !isEmpty(item.Tags) || !isEmpty(item.AcademicFocus),
        ContentItemDisplayPosition
      });
    }
  }
  trackLinkClick(_ref) {
    let {
      ClickedLinkUrl,
      ClickedLinkLabel,
      ClickedLinkWasCTA
    } = _ref;
    const {
      items,
      trackEvents
    } = this.props;
    const {
      openItemIdx
    } = this.state;
    const item = items[openItemIdx];
    if (!isUndefined(trackEvents.onLinkClick)) {
      trackEvents.onLinkClick({
        Scid: item.Scid,
        ContentItemId: item.Id,
        ContentItemWasTargeted: !isEmpty(item.Tags) || !isEmpty(item.AcademicFocus),
        ContentItemDisplayPosition: openItemIdx,
        ClickedLinkUrl,
        ClickedLinkLabel,
        ClickedLinkWasCTA
      });
    }
  }
  render() {
    const {
      items,
      type,
      hostedRootPath,
      onSessionsContentClick
    } = this.props;
    const {
      openItemIdx
    } = this.state;
    return /*#__PURE__*/jsxs(React.Fragment, {
      children: [/*#__PURE__*/jsx(Grid, {
        container: true,
        spacing: 3,
        children: map(items, (item, idx) => /*#__PURE__*/jsx(Grid, {
          item: true,
          xs: 12,
          sm: 6,
          md: 4,
          children: /*#__PURE__*/jsx(ContentItemCard, {
            idx: idx,
            scid: item.Scid,
            type: type,
            id: item.Id,
            title: item.Title,
            contentImgAlt: item.AltText || item.Title,
            description: item.Description,
            collegeEvent: /*#__PURE__*/jsx(Event, {
              item: item,
              stackSegments: true
            }),
            collegeName: item.CollegeName,
            collegeLocation: getLocation(item),
            url: item.Link,
            imageUrl: getImageUrl(item),
            tags: item.Tags,
            foci: item.AcademicFocus,
            onToggleSelect: () => this.selectItem(idx, 'profile'),
            onSessionsContentClick: onSessionsContentClick,
            hostedRootPath: hostedRootPath
          })
        }, item.Id))
      }), /*#__PURE__*/jsx(ContentItemModal$1, {
        itemsCount: size(items),
        item: get(items, `[${openItemIdx}]`),
        hostedRootPath: hostedRootPath,
        openItemIdx: openItemIdx,
        onChange: openItemIdx => this.selectItem(openItemIdx, 'modal'),
        onTrackLinkClick: args => this.trackLinkClick(args),
        type: type
      })]
    });
  }
}
_defineProperty(ContentListDisplay, "propTypes", {
  type: PropTypes.string.isRequired,
  items: PropTypes.array.isRequired,
  hostedRootPath: PropTypes.string.isRequired,
  trackEvents: PropTypes.object,
  contentLaunchedVia: PropTypes.string.isRequired,
  onSessionsContentClick: PropTypes.func
});
_defineProperty(ContentListDisplay, "defaultProps", {
  items: [],
  trackEvents: {},
  contentLaunchedVia: 'profile'
});

const getBaseTrackingData = function () {
  let item = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  let ContentItemDisplayPosition = arguments.length > 1 ? arguments[1] : undefined;
  return {
    Scid: item.Scid,
    ContentItemId: item.Id,
    ContentItemWasTargeted: !isEmpty(item.Tags) || !isEmpty(item.AcademicFocus),
    ContentItemDisplayPosition
  };
};
const getAt = (arr, idx) => {
  if (isUndefined(arr) || isUndefined(idx)) {
    return undefined;
  }
  return arr[idx];
};
const useModalManager = _ref => {
  let {
    results,
    getCollegeUrl,
    getCollegeAction,
    onLinkClick,
    onViewContent
  } = _ref;
  const [getMetadata] = useMetadata();
  const [openIdxs, setOpenIdxs] = useState([]);
  const {
    collegeIdx,
    openItemIdx,
    openCollegeUrl,
    openCollegeAction,
    openCollegeContent,
    openCollegeContentCount,
    openCollegeContentItem
  } = useMemo(() => {
    const collegeIdx = openIdxs[0];
    const openItemIdx = openIdxs[1];
    const openCollege = getAt(results, collegeIdx);
    const openCollegeContent = get(openCollege, `Content`);
    const openCollegeContentCount = size(openCollegeContent);
    const openCollegeContentItem = getAt(openCollegeContent, openIdxs[1]);
    const openCollegeUrl = getCollegeUrl(openCollege?.Scid, openCollegeContentItem?.Id);
    const openCollegeAction = getCollegeAction ? getCollegeAction(openCollege) : null;
    return {
      collegeIdx,
      openItemIdx,
      openCollege,
      openCollegeUrl,
      openCollegeAction,
      openCollegeContent,
      openCollegeContentCount,
      openCollegeContentItem
    };
  }, [results, openIdxs]);
  const onTrackViewContent = useCallback((item, ContentLaunchedVia) => {
    const shouldTrackView = !isUndefined(onViewContent) && !isEmpty(item);
    if (shouldTrackView) {
      onViewContent(item, {
        ...getBaseTrackingData(item, openItemIdx),
        ContentLaunchedVia,
        ...getMetadata()
      });
    }
  }, [onViewContent, openItemIdx]);
  const onOpenModal = useCallback((resultIdx, openItemIdx) => {
    setOpenIdxs([resultIdx, openItemIdx]);
    const result = get(results, `[${resultIdx}]`);
    const item = get(result, `Content[${openItemIdx}]`);
    onTrackViewContent(item, 'Card');
  }, [onTrackViewContent, results]);
  const onModalIdxChange = useCallback(openItemIdx => {
    setOpenIdxs([collegeIdx, openItemIdx]);
    const item = get(openCollegeContent, `[${openItemIdx}]`);
    onTrackViewContent(item, 'Modal');
  }, [onTrackViewContent, openCollegeContent]);
  const onModalLinkClick = useCallback(_ref2 => {
    let {
      ClickedLinkUrl,
      ClickedLinkLabel,
      ClickedLinkWasCTA
    } = _ref2;
    if (!isUndefined(onLinkClick)) {
      onLinkClick(openCollegeContentItem, {
        ...getBaseTrackingData(openCollegeContentItem, openItemIdx),
        ClickedLinkUrl,
        ClickedLinkLabel,
        ClickedLinkWasCTA,
        ContentLaunchedVia: 'Modal',
        ...getMetadata()
      });
    }
  }, [onLinkClick, openCollegeContentItem]);
  return {
    onOpenModal,
    openCollegeContentItem,
    openCollegeUrl,
    openCollegeAction,
    openCollegeContentCount,
    openCollegeIdx: collegeIdx,
    openContentIdx: openItemIdx,
    onModalIdxChange,
    onModalLinkClick
  };
};

const useStyles$9 = makeStyles(() => ({
  card: {
    '& > div > div:nth-child(3)': {
      borderRadius: 0
    }
  }
}));
const HIDDEN = 'hidden';
const VISIBLE = 'visible';
const DELAY = 250;
const ContentItemCardAnimationWrapper = _ref => {
  let {
    isCurrent,
    left,
    count,
    idx,
    children
  } = _ref;
  const classes = useStyles$9();
  const [visibility, setVisibility] = useState(HIDDEN);
  const isVisible = visibility === VISIBLE;
  useEffect(() => {
    if (isCurrent) {
      setVisibility(VISIBLE);
    } else if (isVisible) {
      setTimeout(() => setVisibility(HIDDEN), DELAY + 1);
    }
  }, [isCurrent]);
  return /*#__PURE__*/jsx(Box, {
    className: classes.card,
    width: "100%",
    left: left,
    "aria-hidden": !isCurrent,
    top: isCurrent ? '0px' : '75px',
    position: isCurrent ? 'relative' : 'absolute',
    "aria-label": isCurrent ? `Currently viewing ${idx + 1} of ${count}` : '',
    style: {
      transition: `left ${DELAY}ms ease-out`,
      visibility
    },
    children: children
  }, idx);
};
ContentItemCardAnimationWrapper.propTypes = {
  isCurrent: PropTypes.bool,
  isAfter: PropTypes.bool,
  isBefore: PropTypes.bool,
  left: PropTypes.string,
  count: PropTypes.number,
  idx: PropTypes.number,
  children: PropTypes.node
};

const TrackedContentItemCard = _ref => {
  let {
    key,
    scid,
    contentId,
    tags,
    foci,
    url,
    TrackedImpressionComponent = React.Fragment,
    ...rest
  } = _ref;
  return /*#__PURE__*/jsx(Metadata, {
    value: {
      scid: scid,
      locationIndex: key,
      contentItemId: contentId,
      contentItemWasTargeted: !isEmpty(tags) || !isEmpty(foci),
      videoContent: isVideoLink(url)
    },
    children: /*#__PURE__*/jsx(TrackedImpressionComponent, {
      children: /*#__PURE__*/jsx(ContentItemCard, {
        foci: foci,
        tags: tags,
        url: url,
        ...rest
      }, key + '_' + contentId)
    })
  });
};
TrackedContentItemCard.propTypes = {
  key: PropTypes.any,
  scid: PropTypes.number,
  contentId: PropTypes.string,
  tags: PropTypes.array,
  foci: PropTypes.array,
  url: PropTypes.string,
  TrackedImpressionComponent: PropTypes.elementType
};

const ContentItemCardStackContent = _ref => {
  let {
    contentType,
    currentIdx,
    hostedRootPath,
    getTagPillProps,
    TrackedImpressionComponent,
    Scid,
    count,
    item,
    idx,
    open
  } = _ref;
  const isCurrent = idx === currentIdx;
  const isRightBefore = idx === currentIdx - 1;
  const isRightAfter = idx === currentIdx + 1;
  if (!isCurrent && !isRightBefore && !isRightAfter) {
    return null;
  }
  const isBefore = idx < currentIdx;
  const isAfter = idx > currentIdx;
  const left = useMemo(() => isBefore ? '-110%' : isAfter ? '110%' : '0px', [isBefore, isAfter]);
  const openModal = useCallback(() => open(idx, 'search-result'), [idx, open]);
  const imageUrl = useMemo(() => item.ImageUrl ? getImageUrl(item) : undefined, [item]);
  const {
    CardComponent,
    additionalProps
  } = useMemo(() => {
    if (isCurrent) {
      return {
        CardComponent: TrackedContentItemCard,
        additionalProps: {
          TrackedImpressionComponent,
          scid: Scid,
          contentId: item.Id
        }
      };
    }
    return {
      CardComponent: ContentItemCard,
      additionalProps: {}
    };
  });
  return /*#__PURE__*/jsx(ContentItemCardAnimationWrapper, {
    isCurrent: isCurrent,
    left: left,
    count: count,
    idx: idx,
    children: /*#__PURE__*/jsx(CardComponent, {
      description: item.Description,
      imageUrl: imageUrl,
      foci: item.AcademicFocus,
      tags: item.Tags,
      title: item.Title,
      contentImgAlt: item.AltText || item.Title,
      url: item.Link,
      type: contentType,
      hostedRootPath: hostedRootPath,
      onToggleSelect: openModal,
      getTagPillProps: getTagPillProps,
      onShowMoreTags: openModal,
      collegeEvent: /*#__PURE__*/jsx(Event, {
        item: item,
        stackSegments: true
      }),
      ...additionalProps
    })
  }, idx);
};
ContentItemCardStackContent.propTypes = {
  contentType: PropTypes.string,
  currentIdx: PropTypes.number,
  hostedRootPath: PropTypes.string,
  getTagPillProps: PropTypes.func,
  TrackedImpressionComponent: PropTypes.elementType,
  Scid: PropTypes.number,
  count: PropTypes.number,
  item: PropTypes.object,
  idx: PropTypes.number,
  open: PropTypes.func
};

const useStyles$8 = makeStyles(theme => {
  return {
    topSection: {
      background: '#fff',
      borderBottomLeftRadius: '0px',
      borderBottomRightRadius: '0px',
      '& a': {
        color: 'inherit',
        cursor: 'pointer',
        '&:hover, &:focus': {
          cursor: 'pointer',
          color: theme.palette.primary.main
        }
      }
    },
    avatar: {
      width: '40px',
      height: '40px',
      marginRight: theme.spacing(2)
    },
    buttonsWrapper: {
      pointerEvents: 'none'
    },
    collegeAction: {
      '&:not(:empty):before': {
        content: '"•"',
        display: 'inline-block',
        margin: '0 8px'
      }
    },
    lrbtn: {
      margin: '8px',
      color: 'white',
      width: '2.25em',
      height: '2.25em',
      pointerEvents: 'auto',
      background: 'rgba(31, 31, 31, 0.6)',
      transition: 'all 0.25s ease-in-out',
      '&:hover, &:focus': {
        background: 'rgba(31, 31, 31, 0.8)'
      },
      '&:before': {
        top: '0',
        content: '""',
        width: '90px',
        height: '290',
        padding: '16px',
        display: 'block',
        marginTop: '-120px',
        position: 'absolute'
      },
      '&.Mui-disabled': {
        opacity: 0
      }
    }
  };
});
const ContentItemCardStack = _ref => {
  let {
    onOpenModal,
    contentType,
    college,
    getCollegeAction,
    hostedRootPath,
    getCollegeUrl,
    getTagPillProps,
    TrackedImpressionComponent
  } = _ref;
  const classes = useStyles$8();
  const [currentIdx, setCurrentIdx] = useState(0);
  const {
    Name,
    Scid,
    content,
    count
  } = useMemo(() => {
    const content = get(college, 'Content', []);
    return {
      Name: get(college, 'Name'),
      Scid: get(college, 'Scid'),
      content,
      count: size(content)
    };
  }, [college]);
  const collegeAction = useMemo(() => {
    return getCollegeAction ? getCollegeAction(college) : null;
  }, [college]);
  const contentItemId = useMemo(() => get(content, `[${currentIdx}].Id`), [content, currentIdx]);
  const url = getCollegeUrl(Scid, contentItemId);
  return /*#__PURE__*/jsx(Paper, {
    elevation: 1,
    children: /*#__PURE__*/jsxs(Box, {
      width: "100%",
      overflow: "hidden",
      position: "relative",
      borderRadius: "4px",
      zIndex: 0,
      children: [/*#__PURE__*/jsx(Box, {
        p: 1,
        className: classes.topSection,
        children: /*#__PURE__*/jsx(AvatarWithTitle, {
          avatar: /*#__PURE__*/jsx(Link, {
            to: url,
            children: /*#__PURE__*/jsx(CrestLoader, {
              scid: Scid,
              className: classes.avatar
            })
          }),
          title: /*#__PURE__*/jsx(Box, {
            mt: -1,
            children: /*#__PURE__*/jsxs(Typography, {
              align: "left",
              component: "span",
              variant: "body1",
              display: "block",
              "aria-label": "View College Profile",
              children: [/*#__PURE__*/jsx(Link, {
                to: url,
                children: Name
              }), !collegeAction ? null : /*#__PURE__*/jsx(Metadata, {
                value: {
                  contentItemId
                },
                children: /*#__PURE__*/jsx("span", {
                  className: classes.collegeAction,
                  children: collegeAction
                })
              })]
            })
          })
        })
      }), count < 2 ? null : /*#__PURE__*/jsxs(Box, {
        marginTop: "110px",
        width: "100%",
        zIndex: "2",
        position: "absolute",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        className: classes.buttonsWrapper,
        children: [/*#__PURE__*/jsx(IconButton, {
          className: classes.lrbtn,
          "aria-label": `Previous Content Card for ${Name}`,
          onClick: () => setCurrentIdx(currentIdx - 1),
          disabled: currentIdx === 0,
          children: /*#__PURE__*/jsx(FontAwesomeIcon, {
            icon: faArrowLeft$1,
            size: "xs"
          })
        }), /*#__PURE__*/jsx(IconButton, {
          className: classes.lrbtn,
          "aria-label": `Next Content Card for ${Name}`,
          onClick: () => setCurrentIdx(currentIdx + 1),
          disabled: currentIdx === content.length - 1,
          children: /*#__PURE__*/jsx(FontAwesomeIcon, {
            icon: faArrowRight$1,
            size: "xs"
          })
        })]
      }), map(content, (item, idx) => {
        return /*#__PURE__*/jsx(ContentItemCardStackContent, {
          item: item,
          idx: idx,
          open: () => onOpenModal(idx),
          getTagPillProps: getTagPillProps,
          collegeUrl: url,
          TrackedImpressionComponent: TrackedImpressionComponent,
          contentType: contentType,
          currentIdx: currentIdx,
          hostedRootPath: hostedRootPath,
          Scid: Scid,
          count: count
        }, idx);
      })]
    })
  });
};
ContentItemCardStack.propTypes = {
  onOpenModal: PropTypes.func,
  contentType: PropTypes.string,
  college: PropTypes.object,
  getCollegeAction: PropTypes.func,
  hostedRootPath: PropTypes.string,
  getCollegeUrl: PropTypes.func,
  onViewContent: PropTypes.func,
  onLinkClick: PropTypes.func,
  getTagPillProps: PropTypes.func,
  TrackedImpressionComponent: PropTypes.elementType
};

const GUTTER_SIZE = 3;
const useStyles$7 = makeStyles(theme => {
  return {
    gridItem: {
      marginBottom: theme.spacing(GUTTER_SIZE)
    },
    [theme.breakpoints.up('md')]: {
      gridSizer: {
        width: 'calc(24% - 8px)',
        '&.three-column': {
          width: 'calc(32% - 4px)'
        }
      }
    },
    [theme.breakpoints.down('md')]: {
      gridSizer: {
        width: 'calc(32% - 8px)'
      }
    },
    [theme.breakpoints.down('sm')]: {
      gridSizer: {
        width: 'calc(49% - 8px)'
      }
    },
    [theme.breakpoints.down('xs')]: {
      gridSizer: {
        width: '100%'
      }
    }
  };
});
const ContentItemsInMasonry = _ref => {
  let {
    results = [],
    hostedRootPath,
    contentType,
    getCollegeAction,
    getCollegeUrl,
    onLinkClick,
    onViewContent,
    getTagPillProps,
    TrackedImpressionComponent
  } = _ref;
  const classes = useStyles$7();
  const theme = useTheme();
  const columnClass = 'three-column';
  const className = useMemo(() => cn(classes.gridItem, classes.gridSizer, columnClass), [classes.gridItem, classes.gridSizer, columnClass]);
  const {
    onOpenModal,
    onModalIdxChange,
    onModalLinkClick,
    openContentIdx,
    openCollegeUrl,
    openCollegeAction,
    openCollegeContentItem,
    openCollegeContentCount
  } = useModalManager({
    results,
    onLinkClick,
    onViewContent,
    getCollegeUrl,
    getCollegeAction
  });
  return jsxs(Fragment, {
    children: [jsxs(Masonry, {
      options: {
        columnWidth: `.${classes.gridSizer}`,
        itemSelector: `.${classes.gridItem}`,
        percentPosition: false,
        gutter: theme.spacing(2)
      },
      enableResizableChildren: true,
      children: [jsx("div", {
        className: cn(classes.gridSizer, columnClass)
      }), map(results, (college, idx) => {
        return jsx("div", {
          className: className,
          children: jsx(ContentItemCardStack, {
            onOpenModal: contentIdx => onOpenModal(idx, contentIdx),
            contentType: contentType,
            college: college,
            getCollegeAction: getCollegeAction,
            hostedRootPath: hostedRootPath,
            getCollegeUrl: getCollegeUrl,
            getTagPillProps: getTagPillProps,
            TrackedImpressionComponent: TrackedImpressionComponent
          })
        }, `${college.Scid}-${idx}`);
      })]
    }), jsx(ContentItemModal$1, {
      collegeAction: openCollegeAction,
      type: contentType,
      itemsCount: openCollegeContentCount,
      item: openCollegeContentItem,
      hostedRootPath: hostedRootPath,
      openItemIdx: openContentIdx,
      getTagPillProps: getTagPillProps,
      onChange: onModalIdxChange,
      onTrackLinkClick: onModalLinkClick,
      collegeUrl: openCollegeUrl
    })]
  });
};

const useStyles$6 = makeStyles({
  backdrop: {
    background: 'rgba(255,255,255,0.5)'
  }
});
/**
 * This component displays a white backdrop and loading indicator
 * @param {object} props
 * @returns Loading scrim
 */
const LoadingState = _ref => {
  let {
    loading = false
  } = _ref;
  const classes = useStyles$6();
  return jsx(Box, {
    position: "fixed",
    top: 0,
    left: 0,
    children: jsx(Backdrop, {
      className: classes.backdrop,
      open: loading,
      title: "Loading...",
      children: jsx(Box, {
        height: "100vh",
        width: "100w",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        children: !loading ? null : jsx(CircularProgress, {
          "aria-label": "Loading..."
        })
      })
    })
  });
};

const fociToOptions$1 = (foci, key) => {
  return foci.map(focus => {
    var _a, _b, _c, _d;
    return {
      label: focus,
      value: focus,
      key: key,
      icon: key === 'Culture' ? ((_a = academicFocusIcons['Culture']) === null || _a === void 0 ? void 0 : _a.icon) || null : ((_b = academicFocusIcons[focus]) === null || _b === void 0 ? void 0 : _b.icon) || null,
      selectedIcon: key === 'Culture' ? ((_c = academicFocusIcons['Culture']) === null || _c === void 0 ? void 0 : _c.selectedIcon) || null : ((_d = academicFocusIcons[focus]) === null || _d === void 0 ? void 0 : _d.selectedIcon) || null
    };
  });
};
const TopicFilter = _ref => {
  let {
    topics,
    selected,
    onUpdateSelection
  } = _ref;
  const getOptions = () => {
    const topicOptions = [];
    if (topics) {
      if (topics['AcademicFocus']) {
        const AcademicFocusKeys = [];
        Object.keys(topics['AcademicFocus']).forEach(key => {
          if (topics['AcademicFocus'][key]) {
            AcademicFocusKeys.push(key);
          }
        });
        if (AcademicFocusKeys.length > 0) topicOptions.push({
          key: 'Academic Focus',
          options: AcademicFocusKeys
        });
      }
      if (topics['Categories']) {
        Object.keys(topics['Categories']).forEach(key => {
          const topic = key;
          const topicKeys = [];
          Object.keys(topics['Categories'][key]).forEach(topic => {
            if (topics['Categories'][key][topic]) {
              topicKeys.push(topic);
            }
          });
          if (topicKeys.length > 0) topicOptions.push({
            key: topic,
            options: topicKeys
          });
        });
      }
    }
    return topicOptions;
  };
  const getSelectedFormatted = useCallback(() => {
    const flatSelectedTopics = [];
    const afs = (selected === null || selected === void 0 ? void 0 : selected.AcademicFocus) ? fociToOptions$1(selected === null || selected === void 0 ? void 0 : selected.AcademicFocus, 'Academic Focus') : [];
    const cs = (selected === null || selected === void 0 ? void 0 : selected.Categories) ? fociToOptions$1(selected.Categories || [], 'Categories') : [];
    if (afs.length) flatSelectedTopics.push(...afs);
    if (cs.length) flatSelectedTopics.push(...cs);
    return flatSelectedTopics;
  }, [selected]);
  const updateSelected = useCallback(selected => {
    const AcademicFocus = [];
    const Categories = [];
    selected.forEach(s => {
      if (s.key === 'Academic Focus') {
        AcademicFocus.push(s.value);
      } else {
        Categories.push(s.value);
      }
    });
    const finalSelected = {
      AcademicFocus: AcademicFocus,
      Categories: Categories
    };
    onUpdateSelection(finalSelected);
  }, [selected, onUpdateSelection]);
  return jsxs(Box, {
    mx: 2,
    mb: 2,
    children: [jsx(Typography, {
      variant: "body2",
      color: "textSecondary",
      children: "Expand your search to see more filters"
    }), getOptions().map((option, index) => {
      return jsxs(React.Fragment, {
        children: [jsx(ListSubheader, {
          disableSticky: true,
          disableGutters: true,
          children: option.key
        }), jsx(Box, {
          mb: 2,
          children: jsx(ChipSelector, {
            options: fociToOptions$1(option.options, option.key),
            selected: getSelectedFormatted(),
            onUpdateSelection: updateSelected
          }, option.key)
        })]
      }, index);
    })]
  });
};

const HostedByFilter = _ref => {
  let {
    selected,
    onUpdateSelection
  } = _ref;
  const updateSelected = event => {
    onUpdateSelection(event.target.value);
  };
  return jsx(Box, {
    mx: 2,
    mb: 2,
    children: jsxs(RadioGroup, {
      "aria-label": "hosted by",
      value: selected,
      onChange: updateSelected,
      children: [jsx(FormControlLabel, {
        value: "all",
        control: jsx(Radio, {
          color: "primary"
        }),
        label: "All Colleges"
      }), jsx(FormControlLabel, {
        value: "myColleges",
        control: jsx(Radio, {
          color: "primary"
        }),
        label: "My Colleges"
      }), jsx(FormControlLabel, {
        value: "forYou",
        control: jsx(Radio, {
          color: "primary"
        }),
        label: "Colleges for You"
      })]
    })
  });
};

const TypeFilter = _ref => {
  let {
    onTypeChange,
    location,
    occurrence
  } = _ref;
  return jsxs(Box, {
    mx: 2,
    mb: 2,
    children: [jsx(Box, {
      py: 2,
      children: jsx(Typography, {
        variant: "subtitle2",
        children: "Location"
      })
    }), jsx(SelectableIconChips, {
      gridItemProps: {
        xs: 6
      },
      onSelect: onTypeChange,
      options: [{
        title: 'In Person',
        faIcon: faMapMarkerAlt$1,
        value: 'InPerson',
        selected: location === 'InPerson'
      }, {
        title: 'Virtual',
        faIcon: faVideo$1,
        value: 'IsVirtual',
        selected: location === 'IsVirtual'
      }]
    }), jsx(Box, {
      py: 2,
      children: jsx(Typography, {
        variant: "subtitle2",
        children: "Occurrence"
      })
    }), jsx(SelectableIconChips, {
      gridItemProps: {
        xs: 6
      },
      onSelect: onTypeChange,
      options: [{
        title: 'One Time',
        faIcon: faCalendar,
        value: 'OneTime',
        selected: occurrence === 'OneTime'
      }, {
        title: 'Recurring',
        faIcon: faRedo,
        value: 'IsRecurring',
        selected: occurrence === 'IsRecurring'
      }]
    })]
  });
};

const EventSearchFilters = _ref => {
  let {
    marginTop,
    verticalNavOffset,
    filters,
    canShowMyCollegesFilter
  } = _ref;
  const {
    hostedBy,
    location,
    occurrence,
    setHostedBy,
    setLocation,
    setOccurrence,
    setTopics,
    topics,
    selectedTopics
  } = filters;
  const getSelectedHostedBy = () => {
    switch (hostedBy) {
      case 'all':
        return ['All Colleges'];
      case 'myColleges':
        return ['My Colleges'];
      case 'forYou':
        return ['Colleges for You'];
      default:
        return null;
    }
  };
  const getSelectedTypes = () => {
    const selectedTypes = [];
    if (location === 'InPerson') {
      selectedTypes.push('In Person');
    }
    if (location === 'IsVirtual') {
      selectedTypes.push('Virtual');
    }
    if (occurrence === 'IsRecurring') {
      selectedTypes.push('Recurring');
    }
    if (occurrence === 'OneTime') {
      selectedTypes.push('One Time');
    }
    return selectedTypes;
  };
  const getSelectedTopics = () => {
    const flatSelectedTopics = [];
    (selectedTopics === null || selectedTopics === void 0 ? void 0 : selectedTopics.AcademicFocus.length) ? flatSelectedTopics.push(...selectedTopics.AcademicFocus) : null;
    (selectedTopics === null || selectedTopics === void 0 ? void 0 : selectedTopics.Categories.length) ? flatSelectedTopics.push(...selectedTopics.Categories) : null;
    return flatSelectedTopics;
  };
  const handleClearType = useCallback(() => {
    setLocation();
    setOccurrence();
  }, [setLocation, setOccurrence]);
  const handleTypeChange = useCallback(value => {
    if (value === 'IsVirtual' || value === 'InPerson') {
      if (location === value) {
        setLocation();
      } else setLocation(value);
    } else if (value === 'IsRecurring' || value === 'OneTime') {
      if (occurrence === value) {
        setOccurrence();
      } else {
        setOccurrence(value);
      }
    }
  }, [location, occurrence, setLocation, setOccurrence]);
  const handleTopicChange = useCallback(value => {
    if (value) setTopics(value);
  }, [setTopics]);
  const handleClearTopic = useCallback(() => {
    setTopics({
      AcademicFocus: [],
      Categories: []
    });
  }, [setTopics]);
  return jsx(Box, {
    height: 48,
    children: jsx(ScrollHideAppBar, {
      marginTop: marginTop,
      appBarProps: {
        color: 'inherit',
        variant: 'outlined'
      },
      children: jsx(Box, {
        paddingLeft: verticalNavOffset ? `${verticalNavOffset}px` : undefined,
        children: jsxs(Box, {
          display: "flex",
          p: 2,
          children: [canShowMyCollegesFilter && setHostedBy && jsx(Dropdown, {
            fieldLabel: "Hosted By",
            buttonLabel: "Hosted By",
            selectionLabels: getSelectedHostedBy(),
            children: jsx(HostedByFilter, {
              selected: hostedBy,
              onUpdateSelection: setHostedBy
            })
          }), jsx(Dropdown, {
            fieldLabel: "Topics",
            buttonLabel: "Topics",
            selectionLabels: getSelectedTopics(),
            onClear: handleClearTopic,
            children: jsx(TopicFilter, {
              topics: topics,
              selected: selectedTopics,
              onUpdateSelection: handleTopicChange
            })
          }), jsx(Dropdown, {
            fieldLabel: "Type",
            buttonLabel: "Type",
            selectionLabels: getSelectedTypes(),
            onClear: handleClearType,
            children: jsx(TypeFilter, {
              onTypeChange: handleTypeChange,
              location: location,
              occurrence: occurrence
            })
          })]
        })
      }, verticalNavOffset)
    })
  });
};

const useStyles$5 = makeStyles(() => ({
  gridItems: {
    flex: 1
  }
}));
const fociToOptions = foci => {
  return foci.map(focus => {
    var _a, _b;
    return {
      label: focus,
      value: focus,
      key: focus,
      icon: (_a = academicFocusIcons[focus]) === null || _a === void 0 ? void 0 : _a.icon,
      selectedIcon: (_b = academicFocusIcons[focus]) === null || _b === void 0 ? void 0 : _b.selectedIcon
    };
  });
};
const AcademicsFilter = _ref => {
  let {
    creditsAwarded,
    onCreditsChange,
    focusOptions,
    academicFocusesSelected,
    onUpdateFocusSelection
  } = _ref;
  const classes = useStyles$5();
  const updateSelected = useCallback(selected => {
    const AcademicFocus = [];
    selected.forEach(s => {
      AcademicFocus.push(s.value);
    });
    onUpdateFocusSelection(AcademicFocus);
  }, [onUpdateFocusSelection]);
  return jsxs(Box, {
    mx: 2,
    mb: 2,
    children: [jsx(Box, {
      py: 2,
      children: jsx(Typography, {
        variant: "subtitle2",
        children: "Credits"
      })
    }), jsx(Box, {
      pb: 2,
      children: jsx(SelectableIconChips, {
        gridItemProps: {
          className: classes.gridItems
        },
        onSelect: onCreditsChange,
        options: [{
          title: 'Credits Awarded',
          faIcon: faDiploma,
          value: true,
          selected: creditsAwarded
        }, {
          title: 'Non-Credit',
          faIcon: faAward$1,
          value: false,
          selected: creditsAwarded !== undefined && creditsAwarded == false
        }]
      })
    }), jsxs(Box, {
      py: 2,
      children: [jsx(Typography, {
        variant: "subtitle2",
        children: "Academic Focus"
      }), jsx(Typography, {
        variant: "body2",
        color: "textSecondary",
        children: (focusOptions === null || focusOptions === void 0 ? void 0 : focusOptions.length) > 0 ? 'Focus of the program or specific courses offered' : 'Expand your search to see more filters'
      })]
    }), jsx(ChipSelector, {
      options: fociToOptions(focusOptions),
      selected: fociToOptions(academicFocusesSelected || []),
      onUpdateSelection: updateSelected
    })]
  });
};

const eligibilityToOptions = options => {
  return options.map(option => {
    return {
      label: option,
      value: option,
      key: option
    };
  });
};
const allEligibility = ['6th', '7th', '8th', '9th', '10th', '11th', '12th', 'High School Graduate'];
const earlyCareerReadinessEligibility = ['6th', '7th', '8th', '9th'];
const DetailsFilter = _ref => {
  let {
    financialAidAvailable,
    isVirtual,
    onEligibilityChange,
    onFinancialAidChange,
    onRoomBoardChange,
    onSeasonChange,
    onVirtualChange,
    roomAndBoardAvailable,
    season,
    selectedEligibility,
    earlyCareerReadinessOnly
  } = _ref;
  const eligibility = earlyCareerReadinessOnly ? earlyCareerReadinessEligibility : allEligibility;
  const updateSelected = useCallback(selected => {
    const Eligibility = [];
    selected.forEach(s => {
      Eligibility.push(s.value);
    });
    onEligibilityChange(Eligibility);
  }, [onEligibilityChange]);
  const handleRoomBoardChange = event => {
    onRoomBoardChange(event.target.checked);
  };
  const handleSeasonChange = useCallback(value => {
    let newSelectedSeasons = season ? [...season] : [];
    if (newSelectedSeasons === null || newSelectedSeasons === void 0 ? void 0 : newSelectedSeasons.includes(value)) {
      newSelectedSeasons = [...newSelectedSeasons.filter(s => s !== value)];
    } else {
      newSelectedSeasons = [...newSelectedSeasons, value];
    }
    onSeasonChange(newSelectedSeasons);
  }, [season, onSeasonChange]);
  const handleFinancialAidChange = event => {
    onFinancialAidChange(event.target.checked);
  };
  return jsxs(Box, {
    mx: 2,
    mb: 2,
    children: [jsx(Box, {
      py: 2,
      children: jsx(Typography, {
        variant: "subtitle2",
        children: "Type"
      })
    }), jsx(SelectableIconChips, {
      gridItemProps: {
        xs: 6
      },
      onSelect: onVirtualChange,
      options: [{
        title: 'In Person',
        faIcon: faMapMarkerAlt$1,
        value: false,
        selected: isVirtual !== undefined && !isVirtual
      }, {
        title: 'Virtual',
        faIcon: faVideo$1,
        value: true,
        selected: isVirtual
      }]
    }), isVirtual === false && jsx(Box, {
      py: 1.5,
      // Override mui default styles
      ml: 1.5,
      children: jsx(FormControlLabel, {
        control: jsx(Checkbox, {
          checked: roomAndBoardAvailable,
          color: "primary",
          onChange: handleRoomBoardChange,
          name: "roomAndBoardAvailable"
        }),
        label: "Room and Board available"
      })
    }), jsx(Box, {
      py: 2,
      children: jsx(Typography, {
        variant: "subtitle2",
        children: "Season"
      })
    }), jsx(Box, {
      display: "flex",
      flexDirection: "column",
      gridGap: 2,
      mb: 3,
      children: jsx(SelectableIconChips, {
        gridItemProps: {
          xs: 6
        },
        onSelect: handleSeasonChange,
        options: [{
          title: 'Winter',
          faIcon: faSnowflake,
          value: 'Winter',
          selected: season === null || season === void 0 ? void 0 : season.includes('Winter')
        }, {
          title: 'Spring',
          faIcon: faFlower,
          value: 'Spring',
          selected: season === null || season === void 0 ? void 0 : season.includes('Spring')
        }, {
          title: 'Summer',
          faIcon: faSun,
          value: 'Summer',
          selected: season === null || season === void 0 ? void 0 : season.includes('Summer')
        }, {
          title: 'Fall',
          faIcon: faLeaf,
          value: 'Fall',
          selected: season === null || season === void 0 ? void 0 : season.includes('Fall')
        }]
      })
    }), jsxs(Box, {
      pt: 2,
      pb: 2.5,
      children: [jsx(Typography, {
        variant: "subtitle2",
        children: "Eligible Grade(s)"
      }), jsx(Typography, {
        variant: "body2",
        color: "textSecondary",
        children: "Current or rising grade at the start of the program."
      })]
    }), jsx(Box, {
      pb: 2,
      children: jsx(ChipSelector, {
        options: eligibilityToOptions(eligibility),
        selected: eligibilityToOptions(selectedEligibility || []),
        onUpdateSelection: updateSelected
      })
    }), jsx(Box, {
      py: 2,
      children: jsx(Typography, {
        variant: "subtitle2",
        children: "Cost & Aid"
      })
    }), jsx(Box
    // Override mui default styles
    , {
      // Override mui default styles
      ml: 1.5,
      children: jsx(FormControlLabel, {
        control: jsx(Checkbox, {
          checked: financialAidAvailable,
          color: "primary",
          onChange: handleFinancialAidChange,
          name: "financialAidAvailable"
        }),
        label: "Financial Aid and Scholarships available"
      })
    })]
  });
};

const ProgramSearchFilters = _ref => {
  let {
    marginTop,
    verticalNavOffset,
    filters,
    canShowMyCollegesFilter,
    earlyCareerReadinessOnly
  } = _ref;
  const {
    academicFocuses,
    creditsAwarded,
    financialAidAvailable,
    hostedBy,
    isVirtual,
    roomAndBoardAvailable,
    season,
    selectedEligibility,
    selectedFocuses,
    setAcademicFocuses,
    setCreditsAwarded,
    setEligibility,
    setFinancialAid,
    setHostedBy,
    setIsVirtual,
    setRoomBoard,
    setSeason
  } = filters;
  const getSelectedHostedBy = () => {
    switch (hostedBy) {
      case 'all':
        return ['All Colleges'];
      case 'myColleges':
        return ['My Colleges'];
      case 'forYou':
        return ['Colleges for You'];
      default:
        return null;
    }
  };
  const getSelectedAcademics = () => {
    let selected = [];
    if (creditsAwarded) {
      selected.push('Credits Awarded');
    }
    if (creditsAwarded !== undefined && creditsAwarded === false) {
      selected.push('Non-Credit');
    }
    if (selectedFocuses && selectedFocuses.length > 0) {
      selected = selected.concat(selectedFocuses);
    }
    return selected;
  };
  const getSelectedDetails = () => {
    let selectedDetails = [];
    if (isVirtual) {
      selectedDetails.push('Virtual');
    }
    if (isVirtual !== undefined && isVirtual === false) {
      selectedDetails.push('In Person');
    }
    if (roomAndBoardAvailable) {
      selectedDetails.push('Room and Board Available');
    }
    if (season && season.length > 0) {
      selectedDetails = selectedDetails.concat(season);
    }
    if (selectedEligibility && selectedEligibility.length > 0) {
      selectedDetails = selectedDetails.concat(selectedEligibility);
    }
    if (financialAidAvailable) {
      selectedDetails.push('Financial Aid and Scholarships Available');
    }
    return selectedDetails;
  };
  const getAcademicFocuses = () => {
    let focusOptions = [];
    if (academicFocuses) {
      const AcademicFocusKeys = [];
      Object.keys(academicFocuses).map(key => {
        if (academicFocuses[key]) {
          AcademicFocusKeys.push(key);
        }
      });
      focusOptions = focusOptions.concat(AcademicFocusKeys);
    }
    return focusOptions;
  };
  const handleAcademicFocusChange = useCallback(value => {
    setAcademicFocuses(value);
  }, [setAcademicFocuses]);
  const handleClearAcademics = useCallback(() => {
    setCreditsAwarded(undefined);
    setAcademicFocuses([]);
  }, []);
  const handleClearDetails = useCallback(() => {
    setIsVirtual(undefined);
    setRoomBoard(undefined);
    setSeason([]);
    setEligibility([]);
    setFinancialAid(undefined);
  }, []);
  return jsx(Box, {
    height: 48,
    children: jsx(ScrollHideAppBar, {
      marginTop: marginTop,
      appBarProps: {
        color: 'inherit',
        variant: 'outlined'
      },
      children: jsx(Box, {
        paddingLeft: verticalNavOffset ? `${verticalNavOffset}px` : undefined,
        children: jsxs(Box, {
          display: "flex",
          p: 2,
          children: [canShowMyCollegesFilter && setHostedBy && jsx(Dropdown, {
            fieldLabel: "Hosted By",
            buttonLabel: "Hosted By",
            selectionLabels: getSelectedHostedBy(),
            children: jsx(HostedByFilter, {
              selected: hostedBy,
              onUpdateSelection: setHostedBy
            })
          }), jsx(Dropdown, {
            fieldLabel: "Academics",
            buttonLabel: "Academics",
            selectionLabels: getSelectedAcademics(),
            onClear: handleClearAcademics,
            children: jsx(AcademicsFilter, {
              creditsAwarded: creditsAwarded,
              onCreditsChange: setCreditsAwarded,
              focusOptions: getAcademicFocuses(),
              academicFocusesSelected: selectedFocuses,
              onUpdateFocusSelection: handleAcademicFocusChange
            })
          }), jsx(Dropdown, {
            fieldLabel: "Details",
            buttonLabel: "Details",
            selectionLabels: getSelectedDetails(),
            onClear: handleClearDetails,
            children: jsx(DetailsFilter, {
              financialAidAvailable: financialAidAvailable,
              isVirtual: isVirtual,
              onEligibilityChange: setEligibility,
              onFinancialAidChange: setFinancialAid,
              onRoomBoardChange: setRoomBoard,
              onSeasonChange: setSeason,
              onVirtualChange: setIsVirtual,
              roomAndBoardAvailable: roomAndBoardAvailable,
              season: season,
              selectedEligibility: selectedEligibility,
              earlyCareerReadinessOnly: earlyCareerReadinessOnly
            })
          })]
        })
      }, verticalNavOffset)
    })
  });
};

const ContentDisplayPage = _ref => {
  let {
    filters,
    isLoading,
    items = [],
    total,
    page,
    pageSize,
    marginTop,
    verticalNavOffset,
    hostedRootPath,
    canShowMyCollegesFilter = false,
    getCollegeAction,
    getCollegeUrl,
    TrackedImpressionComponent,
    onClearFilters,
    onChangePage,
    onViewContent,
    onLinkClick,
    altEmptyState,
    getTagPillProps,
    type,
    title,
    bannerComponent,
    earlyCareerReadinessOnly
  } = _ref;
  const getEmptyState = () => {
    if (altEmptyState) return altEmptyState;
    const emptyStateTitle = type === CONTENT_TYPES.SESSIONS ? 'events' : 'programs';
    return jsx(EmptyStateWithAction, {
      title: `No ${emptyStateTitle} found`,
      subTitle: "Try removing some filters to expand your search",
      icon: faTelescope,
      actionProps: {
        children: 'Clear All Filters',
        onClick: () => onClearFilters()
      }
    });
  };
  const getFilters = () => {
    switch (type) {
      case CONTENT_TYPES.SESSIONS:
        return jsx(EventSearchFilters, {
          filters: filters,
          marginTop: marginTop,
          verticalNavOffset: verticalNavOffset,
          canShowMyCollegesFilter: canShowMyCollegesFilter
        });
      case CONTENT_TYPES.PROGRAMS:
        return jsx(ProgramSearchFilters, {
          earlyCareerReadinessOnly: earlyCareerReadinessOnly,
          filters: filters,
          marginTop: marginTop,
          verticalNavOffset: verticalNavOffset,
          canShowMyCollegesFilter: canShowMyCollegesFilter
        });
      default:
        return null;
    }
  };
  return jsxs(Fragment, {
    children: [getFilters(), jsx(LoadingState, {
      loading: isLoading
    }), jsx(Container, {
      maxWidth: "md",
      children: jsx(Box, {
        mt: 7,
        children: !total && !isLoading ? getEmptyState() : jsxs(React.Fragment, {
          children: [jsxs(Box, {
            minHeight: '60vh',
            mb: 5,
            children: [jsx(SectionTitle, {
              title: title
            }), bannerComponent ? bannerComponent : null, jsx(ContentItemsInMasonry, {
              contentType: type,
              results: items,
              getCollegeAction: getCollegeAction,
              hostedRootPath: hostedRootPath,
              getCollegeUrl: getCollegeUrl,
              onLinkClick: onLinkClick,
              onViewContent: onViewContent,
              TrackedImpressionComponent: TrackedImpressionComponent,
              getTagPillProps: getTagPillProps
            })]
          }), jsx(Box, {
            p: 2,
            pb: 4,
            display: "flex",
            justifyContent: "flex-end",
            children: jsx(Pagination, {
              page: page,
              count: Math.ceil(total / pageSize),
              onChange: (e, page) => {
                onChangePage(page);
                window.scrollTo(0, 0);
              },
              showFirstButton: true,
              showLastButton: true,
              disabled: isLoading || total <= pageSize
            })
          })]
        })
      })
    })]
  });
};

const LowContentBanner = _ref => {
  let {
    message,
    icon = faSparkles
  } = _ref;
  return jsx(Box, {
    mb: 3,
    mr: 2,
    children: jsx(Alert, {
      severity: "info",
      icon: jsx(FontAwesomeIcon, {
        icon: icon,
        size: "xs"
      }),
      children: message
    })
  });
};

var window$1 = window;

const useOnSearch = _ref => {
  let {
    onSearch,
    selectedTag,
    from,
    setResults,
    setLoading,
    setTotals,
    tagChanged
  } = _ref;
  const lastSearchedFrom = useRef();
  const fromChanged = lastSearchedFrom.current !== from;
  const loadContentSearch = useCallback(() => {
    setLoading(true);
    window$1.scrollTo(0, 0);
    onSearch({
      selectedTag,
      from
    }).then(response => {
      const newResults = get(response, 'Colleges', []);
      const totalColleges = get(response, 'TotalCollegeCount', 0);
      const totalPosts = get(response, 'TotalPostCount', 0);
      setResults([newResults]);
      setTotals({
        totalColleges,
        totalPosts
      });
    }).finally(() => {
      setLoading(false);
    });
  }, [onSearch, selectedTag, from, setResults, setLoading, setTotals]);
  useEffect(() => {
    const searchTriggered = tagChanged || fromChanged;
    if (searchTriggered && selectedTag) {
      lastSearchedFrom.current = from;
      loadContentSearch();
    }
  }, [from, loadContentSearch, selectedTag]);
};
const useContentSearch = function (onSearch, selectedTag) {
  let pageSize = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 15;
  let page = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 1;
  let onPageChange = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : noop;
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totals, setTotals] = useState({
    totalColleges: 0,
    totalPosts: 0
  });
  const lastSearchedTag = useRef();
  const tagChanged = lastSearchedTag.current !== selectedTag;
  const from = useMemo(() => (page - 1) * pageSize, [page, pageSize]);
  useEffect(() => {
    if (tagChanged) {
      lastSearchedTag.current = selectedTag;
      onPageChange(1);
    }
  }, [selectedTag, tagChanged, onPageChange]);
  useOnSearch({
    from: tagChanged ? 0 : from,
    onSearch,
    pageSize,
    selectedTag,
    tagChanged,
    setResults,
    setLoading,
    setTotals
  });
  return {
    page,
    pageSize,
    results,
    loading,
    onPageChange,
    totals
  };
};

const Context = /*#__PURE__*/React.createContext();
const ContextProvider = _ref => {
  let {
    children,
    ...props
  } = _ref;
  return /*#__PURE__*/jsx(Context.Provider, {
    value: {
      ...props
    },
    children: children
  });
};
ContextProvider.propTypes = {
  children: PropTypes.node
};

const {
  Body,
  Footer
} = MultiSectionDialog;
const useStyles$4 = makeStyles(() => ({
  categoryContainer: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '24px'
  },
  categoryContainerHeading: {
    marginBottom: '16px'
  },
  categoryList: {
    maxHeight: '700px'
  },
  sectionHeading: {
    marginBottom: '8px',
    marginTop: '16px'
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-start'
  }
}));
const ACADEMIC_FOCUSES_CATEGORY = 'Academic Focuses';
const VIEW_POSTS_EVENT_SOURCE$1 = 'AllTagsModal';
const AllCategoriesDialog = _ref => {
  let {
    onClose,
    onSearch,
    open
  } = _ref;
  const styles = useStyles$4();
  const {
    allTagsByCategory
  } = useContext(Context);
  const studentLifeCategories = omit(allTagsByCategory, ACADEMIC_FOCUSES_CATEGORY);
  const academicFocuses = get(allTagsByCategory, ACADEMIC_FOCUSES_CATEGORY, []);
  const showAcademicFocuses = !isEmpty(academicFocuses);
  const getTagPillProps = useCallback(tag => ({
    onClick: () => {
      onSearch(tag);
    },
    showIcon: true
  }), [onSearch]);
  const categoryPillLists = useMemo(() => {
    const categories = Object.keys(studentLifeCategories);
    return map(categories, category => /*#__PURE__*/jsx(TagPillList, {
      category: category,
      list: get(studentLifeCategories, category, []),
      getTagPillProps: getTagPillProps,
      source: VIEW_POSTS_EVENT_SOURCE$1
    }, category));
  }, [studentLifeCategories, getTagPillProps]);
  return /*#__PURE__*/jsxs(MultiSectionDialog, {
    headerText: "Select A Category",
    scroll: "paper",
    open: open,
    onBackdropClick: onClose,
    onClose: onClose,
    children: [/*#__PURE__*/jsxs(Body, {
      className: styles.categoryList,
      children: [showAcademicFocuses && /*#__PURE__*/jsxs("div", {
        className: styles.categoryContainer,
        style: {
          marginBottom: '12px'
        },
        children: [/*#__PURE__*/jsx(Typography, {
          className: styles.categoryContainerHeading,
          variant: "h6",
          children: "Academic Focuses"
        }), /*#__PURE__*/jsx(TagPillList, {
          list: academicFocuses,
          getTagPillProps: getTagPillProps,
          source: VIEW_POSTS_EVENT_SOURCE$1
        })]
      }), /*#__PURE__*/jsxs("div", {
        className: styles.categoryContainer,
        children: [/*#__PURE__*/jsx(Typography, {
          className: styles.categoryContainerHeading,
          variant: "h6",
          children: "Student Life"
        }), categoryPillLists]
      })]
    }), /*#__PURE__*/jsx(Footer, {
      className: styles.footer,
      children: /*#__PURE__*/jsx(Button, {
        color: "primary",
        onClick: onClose,
        children: "Close"
      })
    })]
  });
};
AllCategoriesDialog.propTypes = {
  onClose: PropTypes.func,
  onSearch: PropTypes.func,
  open: PropTypes.bool
};

const useStyles$3 = makeStyles(() => ({
  allCategoriesTrigger: {
    height: '42px',
    whiteSpace: 'nowrap',
    '&:hover': {
      background: 'rgba(33, 150, 243, 0.2)',
      color: '#004a6c'
    }
  }
}));
const VIEW_POSTS_EVENT_SOURCE = 'AllTagsModal';
const AllCategories = () => {
  const styles = useStyles$3();
  const {
    onTagChange
  } = useContext(Context);
  const {
    open,
    onClose,
    onOpen
  } = useOpenClose(false);
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));
  const onSearch = useCallback(tag => {
    onClose();
    onTagChange(tag, VIEW_POSTS_EVENT_SOURCE);
  }, [onClose, onTagChange]);
  return /*#__PURE__*/jsxs("div", {
    children: [isSm ? /*#__PURE__*/jsx(IconButton, {
      className: styles.allCategoriesTrigger,
      color: "primary",
      onClick: onOpen,
      children: /*#__PURE__*/jsx(FontAwesomeIcon, {
        icon: faEllipsisH
      })
    }) : /*#__PURE__*/jsx(Button, {
      className: styles.allCategoriesTrigger,
      color: "primary",
      startIcon: /*#__PURE__*/jsx(FontAwesomeIcon, {
        icon: faEllipsisH
      }),
      onClick: onOpen,
      children: "\xA0 All Categories"
    }), /*#__PURE__*/jsx(AllCategoriesDialog, {
      onClose: onClose,
      onSearch: onSearch,
      open: open
    })]
  });
};

const useStyles$2 = makeStyles(theme => ({
  root: {
    minWidth: 0,
    minHeight: 0,
    padding: `${theme.spacing(1)}px ${theme.spacing(3)}px`,
    fontSize: '15px',
    whiteSpace: 'nowrap',
    opacity: 1,
    marginRight: theme.spacing(1),
    '&:not(.Mui-disabled), &.Mui-selected': {
      color: '#0A6BB8',
      background: 'rgba(33, 150, 243, 0.08)',
      '&:hover, &:focus-visible': {
        color: '#0A6BB8',
        background: 'rgba(33, 150, 243, 0.2)'
      }
    },
    '&[href], &[href]:hover, &[href]:focus': {
      textDecoration: 'none'
    }
  }
}));

// wrapped in span so baked in MuiButton style doesn't override
const startIcon = /*#__PURE__*/jsx("span", {
  children: /*#__PURE__*/jsx(Box, {
    fontSize: "18px",
    display: "flex",
    children: /*#__PURE__*/jsx(FontAwesomeIcon, {
      icon: faHashtag
    })
  })
});
const getItems = function (tags, classes) {
  let getTagButtonProps = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : noop;
  const tagMap = {};
  const items = map(tags, _ref => {
    let {
      tag
    } = _ref;
    tagMap[tag] = tag;
    return {
      classes,
      label: tag,
      value: tag,
      disableElevation: true,
      component: Button,
      color: 'default',
      variant: 'contained',
      startIcon: startIcon,
      ...getTagButtonProps(tag)
    };
  });
  return {
    tagMap,
    items
  };
};
const VIEW_POSTS_CLICK_EVENT_SOURCE = 'PostsTab';
const TagButtonList = _ref2 => {
  let {
    tags,
    TabsProps,
    getTagButtonProps,
    selectedTag,
    onTagChange,
    ...rest
  } = _ref2;
  const theme = useTheme();
  const classes = useStyles$2();
  const isLargerThanSm = useMediaQuery(theme.breakpoints.up('sm'));
  const {
    tagMap,
    items
  } = useMemo(() => getItems(tags, classes, getTagButtonProps), [tags]);
  return /*#__PURE__*/jsx(Box, {
    width: "100%",
    ...rest,
    children: /*#__PURE__*/jsx(Tabs, {
      scrollButtons: isLargerThanSm ? 'auto' : 'off',
      TabIndicatorProps: {
        style: {
          display: 'none'
        }
      },
      textColor: "inherit",
      items: items,
      value: get(tagMap, selectedTag, false) /* Value must be false if the selected tag */,
      onChange: tag => {
        onTagChange(tag, VIEW_POSTS_CLICK_EVENT_SOURCE);
      },
      ...TabsProps
    })
  });
};
TagButtonList.propTypes = {
  tags: PropTypes.array,
  TabsProps: PropTypes.object,
  getTagButtonProps: PropTypes.func,
  selectedTag: PropTypes.string,
  onTagChange: PropTypes.func
};

const useStyles$1 = makeStyles({
  root: {
    minHeight: 0
  }
});
const FilterBar = () => {
  const {
    myTags: tags,
    selectedTag,
    onTagChange
  } = useContext(Context);
  const classes = useStyles$1();
  return /*#__PURE__*/jsx(Box, {
    width: "100%",
    children: /*#__PURE__*/jsx(TagButtonList, {
      tags: tags,
      selectedTag: selectedTag,
      onTagChange: onTagChange,
      TabsProps: {
        id: 'filter-bar',
        classes
      }
    })
  });
};

const LoadingScrim = () => {
  const {
    loading
  } = useContext(Context);
  return !loading ? null : /*#__PURE__*/jsx(Box, {
    position: "fixed",
    height: "100vh",
    width: "100vw",
    top: "0",
    left: "0",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: "20",
    bgcolor: "rgba(245, 248, 250, 0.5)",
    children: /*#__PURE__*/jsx(CircularProgress, {
      "aria-label": "Loading...",
      size: "40px",
      color: "primary"
    })
  });
};

const ContentItems = () => {
  const ctx = useContext(Context);
  const {
    results = [],
    getCollegeAction,
    hostedRootPath,
    getCollegeUrl,
    onLinkClick,
    onViewContent,
    getTagPillProps,
    TrackedImpressionComponent,
    contentType = CONTENT_TYPES.OVERVIEW
  } = ctx;
  return map(results, (results, idx) => /*#__PURE__*/jsx(ContentItemsInMasonry, {
    contentType: contentType,
    results: results,
    getCollegeAction: getCollegeAction,
    hostedRootPath: hostedRootPath,
    getCollegeUrl: getCollegeUrl,
    onLinkClick: onLinkClick,
    onViewContent: onViewContent,
    getTagPillProps: getTagPillProps,
    TrackedImpressionComponent: TrackedImpressionComponent
  }, idx));
};

const EmptyState = () => {
  return /*#__PURE__*/jsx(Box, {
    children: /*#__PURE__*/jsx(BasicEmptyState, {
      title: "No posts found today",
      subTitle: "Try another category",
      icon: faTelescope
    })
  });
};

const PaginationWrapper = _ref => {
  let {
    children
  } = _ref;
  const {
    totals,
    pageSize,
    page,
    loading,
    onPageChange
  } = useContext(Context);
  const total = totals.totalColleges;
  return /*#__PURE__*/jsxs("div", {
    children: [/*#__PURE__*/jsx("div", {
      children: children
    }), /*#__PURE__*/jsx(Box, {
      p: 2,
      pb: 4,
      display: "flex",
      justifyContent: "flex-end",
      children: /*#__PURE__*/jsx(Pagination, {
        page: page,
        count: Math.ceil(total / pageSize),
        onChange: (e, page) => {
          onPageChange(page);
          window.scrollTo(0, 0);
        },
        showFirstButton: true,
        showLastButton: true,
        disabled: loading || total <= pageSize
      })
    })]
  });
};
PaginationWrapper.propTypes = {
  children: PropTypes.node
};

const Results = () => {
  const {
    selectedTag,
    totals
  } = useContext(Context);
  const hasContent = get(totals, 'totalColleges', 0) > 0;
  return /*#__PURE__*/jsx(Box, {
    mt: 16,
    children: /*#__PURE__*/jsxs(Container, {
      maxWidth: "md",
      children: [/*#__PURE__*/jsxs(Box, {
        display: "flex",
        alignItems: "center",
        children: [/*#__PURE__*/jsx(Avatar, {
          variant: "circular",
          fallbackIcon: /*#__PURE__*/jsx(FontAwesomeIcon, {
            icon: faHashtag
          }),
          iconColor: ICON_COLOR.info
        }), /*#__PURE__*/jsx(Box, {
          ml: 2,
          children: /*#__PURE__*/jsx(Typography, {
            variant: "h6",
            "aria-label": "Selected category",
            children: selectedTag
          })
        })]
      }), /*#__PURE__*/jsx(Box, {
        mt: 4,
        children: hasContent ? /*#__PURE__*/jsx(Box, {
          mb: 12,
          children: /*#__PURE__*/jsx(PaginationWrapper, {
            children: /*#__PURE__*/jsx(ContentItems, {})
          })
        }) : /*#__PURE__*/jsx(EmptyState, {})
      })]
    })
  });
};

const useStyles = makeStyles({
  appBar: {
    backgroundColor: 'white'
  }
});
const PostsPage = _ref => {
  let {
    verticalNavOffset,
    appBarMarginTop,
    onViewDiscoverPosts,
    ...rest
  } = _ref;
  const classes = useStyles();
  const theme = useTheme();
  const isLargerThanSm = useMediaQuery(theme.breakpoints.up('sm'));
  const selectedTag = rest.selectedTag;
  const lastTrackedTag = useRef();
  useEffect(() => {
    if (selectedTag && onViewDiscoverPosts && selectedTag !== lastTrackedTag.current) {
      onViewDiscoverPosts(selectedTag);
      lastTrackedTag.current = selectedTag;
    }
  }, [selectedTag, onViewDiscoverPosts]);
  return /*#__PURE__*/jsx(Box, {
    position: "relative",
    height: "100%",
    minHeight: "100vh",
    children: /*#__PURE__*/jsxs(ContextProvider, {
      ...rest,
      children: [/*#__PURE__*/jsx(ScrollHideAppBar, {
        marginTop: appBarMarginTop,
        appBarProps: {
          color: 'inherit',
          variant: 'outlined',
          className: classes.appBar
        },
        children: /*#__PURE__*/jsx(Box, {
          paddingLeft: verticalNavOffset ? `${verticalNavOffset}px` : undefined,
          children: /*#__PURE__*/jsxs(Box, {
            display: "flex",
            alignItems: "center",
            justifyItems: "space-between",
            width: "100%",
            p: 2,
            pl: isLargerThanSm ? 0 : 2,
            children: [/*#__PURE__*/jsx(Box, {
              flexGrow: 1,
              width: "calc(100% - 140px)",
              children: /*#__PURE__*/jsx(FilterBar, {})
            }), /*#__PURE__*/jsx(Box, {
              flexShrink: 1,
              children: /*#__PURE__*/jsx(AllCategories, {})
            })]
          })
        }, verticalNavOffset)
      }), /*#__PURE__*/jsx(Results, {}), /*#__PURE__*/jsx(LoadingScrim, {})]
    })
  });
};
PostsPage.propTypes = {
  verticalNavOffset: PropTypes.number,
  appBarMarginTop: PropTypes.number,
  onViewDiscoverPosts: PropTypes.func
};

const ContentSearchPreview = props => {
  return /*#__PURE__*/jsx(ContextProvider, {
    ...props,
    children: /*#__PURE__*/jsx(ContentItems, {})
  });
};

export { CONTENT_TYPES, EventSearchFilters as CollegeEventsPage, ContentDisplayPage, ContentItemCard, ContentItemModal$1 as ContentItemModal, ContentListDisplay, ContentSearchPreview, DynamicContentContext, Event, LowContentBanner, PostsPage, TagButtonList, TopicFilter, VideoPreview, earlyCareerReadinessEligibility, getImageUrl as getContentImageUrl, useContentSearch };
//# sourceMappingURL=index.js.map
